import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Accordion } from "react-accessible-accordion";

import testIds from "constants/testIds";
import { classNames } from "helpers/commonHelpers";
import OverlayLoader from "components/atoms/OverlayLoader/OverlayLoader";
import NavContext from "components/molecules/Navigation/components/common/NavContext";
import ConfirmationDialog from "components/organisms/ConfirmationDialog/ConfirmationDialog";
import Alert from "components/v2/Alert/Alert";
import LoadTemplateDialog from "pages/shipper/RequestForm/components/LoadTemplateDialog/LoadTemplateDialog";
import RequestFormAccordion from "@RequestFormV2/components/RequestFormAccordion/RequestFormAccordion";
import RequestFormHeader from "@RequestFormV2/components/RequestFormHeader/RequestFormHeader";
import RequestFormFooter from "@RequestFormV2/components/RequestFormFooter/RequestFormFooter";
import ReviewPage from "@RequestFormV2/components/ReviewPage/ReviewPage";
import SelectPaymentLocationBadge from "@RequestFormV2/components/Badges/SelectPaymentLocationBadge";
import SectionHintBadge from "@RequestFormV2/components/Badges/SectionHintBadge";
import MissingStopsWarningBadge from "@RequestFormV2/components/Badges/MissingStopsWarningBadge";
import ValidationErrorBadge from "@RequestFormV2/components/Badges/ValidationErrorBadge";
import ValidationSuccessBadge from "@RequestFormV2/components/Badges/ValidationSuccessBadge";
import { RequestFormCalculationsProvider } from "@RequestFormV2/context/calculations";
import { RequestFormReviewValidationContext } from "@RequestFormV2/context/reviewValidation";
import { RequestFormSaveProvider, useRequestFormSave } from "@RequestFormV2/context/save";
import { useBottomBannerOffset } from "@RequestFormV2/hooks/useBottomBannerOffset";
import { useMobileHeaderScroll } from "@RequestFormV2/hooks/useMobileHeaderScroll";
import { useQuickRequestEditBootstrap } from "@RequestFormV2/hooks/useQuickRequestEditBootstrap";
import { useRequestFormDraftLoad } from "@RequestFormV2/hooks/useRequestFormDraftLoad";
import { useRequestFormSubmit } from "@RequestFormV2/hooks/useRequestFormSubmit";
import { useRequestFormReview } from "@RequestFormV2/hooks/useRequestFormReview";
import { useReviewValidation } from "@RequestFormV2/hooks/useReviewValidation";
import type { ReviewFieldErrorSnapshot } from "@RequestFormV2/validation/validateOnReview";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { getPackingListMissingStopWarning } from "@RequestFormV2/sections/PackingList/utils/getMissingStopWarning";
import { isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { getRequestFormV2DefaultValues } from "@RequestFormV2/utils/getDefaultFormValues";
import { normalizeLoadedDraftData } from "@RequestFormV2/utils/normalizeDraftSchedule";
import { buildErroredSections } from "@RequestFormV2/utils/buildErroredSections";
import { hasScheduleAddressesFilled } from "@RequestFormV2/utils/hasScheduleAddressesFilled";
import { resolveAccordionBadge, type AccordionBadgeKind } from "@RequestFormV2/utils/resolveAccordionBadge";
import { getSelectedInvitedModeValues } from "@RequestFormV2/utils/invitedModesHelpers";
import usePermissions from "hooks/usePermissions";

import { getRequestFormSections } from "@RequestFormV2/constants";
import { EditShipmentLockProvider } from "@RequestFormV2/context/editShipmentLock";
import { RequestFormModeProvider, useRequestFormMode } from "@RequestFormV2/context/requestFormMode";
import {
  getFormFlow,
  resolveAbandonSectionName,
  setRequestFormAnalyticsContext,
} from "@RequestFormV2/formEvents/requestFormAnalytics";
import { useRequestFormAnalyticsSession } from "@RequestFormV2/formEvents/useRequestFormAnalyticsSession";
import "./RequestFormV2.scss";

const FOOTER_HEIGHT = 72;

const SECTION_HINT_MESSAGES: Record<
  Exclude<AccordionBadgeKind, "validationError" | "validationSuccess" | "selectPaymentLocation" | null>,
  { messageId: string; defaultMessage: string; appearance: "brandHint" | "gray" }
> = {
  selectAddressAndTime: {
    messageId: "request-form.badge.selectAddressAndTime",
    defaultMessage: "Select address and time",
    appearance: "brandHint",
  },
  noPackingList: {
    messageId: "request-form.badge.noPackingList",
    defaultMessage: "No packing list entered",
    appearance: "gray",
  },
  selectFreightMode: {
    messageId: "request-form.badge.selectFreightMode",
    defaultMessage: "Select at least one freight mode",
    appearance: "brandHint",
  },
  setDeadlinesAndProviders: {
    messageId: "request-form.badge.setDeadlinesAndProviders",
    defaultMessage: "Set deadlines and choose providers",
    appearance: "brandHint",
  },
};

function renderAccordionBadge(kind: AccordionBadgeKind) {
  if (kind === "validationError") return <ValidationErrorBadge />;
  if (kind === "validationSuccess") return <ValidationSuccessBadge />;
  if (kind === "selectPaymentLocation") return <SelectPaymentLocationBadge />;
  if (kind) return <SectionHintBadge {...SECTION_HINT_MESSAGES[kind]} />;
  return undefined;
}

function scrollToFirstErroredSection(sectionKey: string | undefined, sections: ReturnType<typeof getRequestFormSections>) {
  if (!sectionKey) return;
  const sectionIndex = sections.findIndex((section) => section.key === sectionKey);
  const accordionEls = document.querySelectorAll(".rfv2-accordion");
  accordionEls[sectionIndex]?.scrollIntoView({ behavior: "smooth", block: "start" });
}

type RequestFormV2ContentProps = {
  methods: UseFormReturn;
  isDraftLoading: boolean;
  isEditQuoteRequest?: boolean;
  isEditShipment?: boolean;
  isEditQuickRequest?: boolean;
};

function RequestFormV2SaveBody({
  methods,
  isDraftLoading,
  isEditQuoteRequest = false,
  isEditShipment = false,
  isEditQuickRequest = false,
}: RequestFormV2ContentProps) {
  const mode = useRequestFormMode();
  const isQuickRequest = mode === "quick";
  const requestFormSections = getRequestFormSections(mode);
  const headerHeadingMessageId = isEditShipment
    ? "new-request.form.editShipment"
    : isEditQuoteRequest
    ? "new-request.form.editQuoteRequest"
    : isEditQuickRequest
    ? "new-request.form.editQuickRequest"
    : isQuickRequest
    ? "new-request.form.newQuickRequest"
    : "request-form.header.newQuoteRequest";
  const intl = useIntl();
  const { navigationConfig, setNavigationConfig } = useContext(NavContext);
  const [searchParams] = useSearchParams();
  const quotesAvailable = searchParams.get("quotesAvailable") === "true";
  const bottomBannerOffset = useBottomBannerOffset();
  const saveContext = useRequestFormSave();
  const { submit, isSubmitting } = useRequestFormSubmit({ isEditQuoteRequest, isEditQuickRequest });
  const hasPaymentLocation = Boolean(methods.watch("organizational.location"));
  const packingList = (useWatch({ control: methods.control, name: "packing_list" }) as PackingListRow[] | undefined) ?? [];
  const scheduleStops =
    (useWatch({ control: methods.control, name: "schedule.stops" }) as ScheduleStopValue[] | undefined) ?? [];
  const invitedModes = useWatch({ control: methods.control, name: "invited_modes" });
  const quotingDeadlineDate = useWatch({ control: methods.control, name: "shipment.quoting_deadline_date" });
  const selectedProviders =
    (useWatch({ control: methods.control, name: "invited_providers.providers" }) as string[] | undefined) ?? [];
  const hasScheduleAddresses = useMemo(() => hasScheduleAddressesFilled(scheduleStops), [scheduleStops]);
  const hasPackingListEntries = useMemo(
    () => packingList.some((row) => !isPackingListRowEmpty(row)),
    [packingList],
  );
  const hasFreightMode = isQuickRequest || getSelectedInvitedModeValues(invitedModes).length > 0;
  const hasDeadlinesAndProviders = Boolean(quotingDeadlineDate) && selectedProviders.length > 0;
  const hasMissingStopsWarning = useMemo(
    () => getPackingListMissingStopWarning(packingList, scheduleStops).hasWarnings,
    [packingList, scheduleStops],
  );
  const missingStopsWarningMessage = intl.formatMessage({
    id: "new-request.schedule.packingList.error.missingPickupOrDelivery",
    defaultMessage: "Some stops were removed. Please update the commodities with a new stop",
  });
  const { erroredSections, hasAttemptedReview, runReviewValidation, clearFieldError, syncErroredSections } =
    useReviewValidation(methods);
  const { handleReview, isReviewValidating } = useRequestFormReview(methods, { runReviewValidation });
  const [expandedUuids, setExpandedUuids] = useState<string[]>(["request-form.organisation"]);
  const [accordionKey, setAccordionKey] = useState(0);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showEmptyPackingListModal, setShowEmptyPackingListModal] = useState(false);
  const isInitialMount = useRef(true);
  const pendingReviewFieldErrorsRef = useRef<ReviewFieldErrorSnapshot[]>([]);
  const isMobileHeaderScrollEnabled = navigationConfig.isMobile && !isReviewMode;
  const isHeaderScrolled = useMobileHeaderScroll(isMobileHeaderScrollEnabled);
  const showMobileMenu = isMobileHeaderScrollEnabled && !isHeaderScrolled;
  const { trackConfirmationBackToEditing } = useRequestFormAnalyticsSession({
    isEditQuoteRequest,
    isEditShipment,
    isQuickRequest,
    isEditQuickRequest,
    isReviewMode,
    expandedUuids,
  });

  const reviewValidationContextValue = useMemo(
    () => ({
      hasAttemptedReview,
      erroredSections,
      clearFieldError,
      syncErroredSections,
    }),
    [hasAttemptedReview, erroredSections, clearFieldError, syncErroredSections],
  );

  useEffect(() => {
    setNavigationConfig((prev: Record<string, unknown>) => ({ ...prev, isRequestFormV2ReviewMode: isReviewMode }));
  }, [isReviewMode, setNavigationConfig]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    document.querySelector(".App")?.scrollTo({ top: 0, behavior: "smooth" });
  }, [isReviewMode]);

  useEffect(() => {
    if (pendingReviewFieldErrorsRef.current.length === 0) return;

    const snapshots = pendingReviewFieldErrorsRef.current;
    pendingReviewFieldErrorsRef.current = [];

    requestAnimationFrame(() => {
      snapshots.forEach(({ path, error }) => methods.setError(path, error));
    });
  }, [accordionKey, methods.setError]);

  const handleExpandAll = () => {
    setExpandedUuids(requestFormSections.map((section) => section.key));
    setAccordionKey((key) => key + 1);
  };

  const expandPackingListSection = () => {
    setExpandedUuids(["request-form.packingList"]);
    setAccordionKey((key) => key + 1);
    requestAnimationFrame(() => scrollToFirstErroredSection("request-form.packingList", requestFormSections));
  };

  const pageStyle = {
    paddingBottom: isReviewMode ? 54 + bottomBannerOffset : FOOTER_HEIGHT + bottomBannerOffset,
  };

  const footerProps = {
    onReviewClick: async () => {
      const result = await handleReview();
      if (result.success) {
        const rows = (methods.getValues("packing_list") ?? []) as PackingListRow[];
        if (rows.every(isPackingListRowEmpty)) {
          setShowEmptyPackingListModal(true);
        } else {
          setIsReviewMode(true);
        }
        return;
      }

      if (result.fieldErrors?.length) {
        pendingReviewFieldErrorsRef.current = result.fieldErrors;
      }

      syncErroredSections();
      const sections = result.erroredSections?.length
        ? result.erroredSections
        : buildErroredSections(methods.formState.errors, mode);

      if (!sections.length && !result.fieldErrors?.length) return;

      setExpandedUuids(sections);
      setAccordionKey((key) => key + 1);
      requestAnimationFrame(() => scrollToFirstErroredSection(sections[0], requestFormSections));
    },
    onEditClick: () => {
      trackConfirmationBackToEditing();
      setIsReviewMode(false);
    },
    onSubmitClick: () => {
      submit().catch(() => undefined);
    },
    onSaveDraftClick: () => saveContext?.flushSave().catch(() => false) ?? Promise.resolve(false),
    currentSection: resolveAbandonSectionName(expandedUuids, isReviewMode),
  };

  return (
    <RequestFormReviewValidationContext.Provider value={reviewValidationContextValue}>
      <RequestFormCalculationsProvider>
        {isDraftLoading && <OverlayLoader />}
        {isSubmitting && <OverlayLoader />}
        {isReviewValidating && <OverlayLoader />}
        <LoadTemplateDialog
          isOpen={templateDialogOpen}
          close={() => setTemplateDialogOpen(false)}
          isQuoteRequest={!isQuickRequest}
          formVersion="v2"
          transformLoadedData={(data) =>
            normalizeLoadedDraftData({
              ...getRequestFormV2DefaultValues(),
              ...(data as Record<string, unknown>),
            })
          }
        />

        <ConfirmationDialog
          open={showEmptyPackingListModal}
          onClose={() => setShowEmptyPackingListModal(false)}
          title={intl.formatMessage({
            id: "new-request.form.packingListConfirmationTitle",
            defaultMessage: "Packing list items not keyed in",
          })}
          description={intl.formatMessage({
            id: "new-request.form.packingListConfirmationDescription",
            defaultMessage: "Are you sure you want to proceed without entering packing list items?",
          })}
          submitBtnText={intl.formatMessage({
            id: "new-request.form.confirmationPrimaryBtnText",
            defaultMessage: "Go back and add items",
          })}
          secondaryBtnText={intl.formatMessage({
            id: "new-request.form.confirmationSecondaryBtnText",
            defaultMessage: "Proceed without items",
          })}
          onSubmit={() => {
            setShowEmptyPackingListModal(false);
            expandPackingListSection();
          }}
          onSecondaryBtnClick={() => {
            setShowEmptyPackingListModal(false);
            setIsReviewMode(true);
          }}
        />

        {!isReviewMode && (
          <RequestFormHeader
            onExpandAll={handleExpandAll}
            onOpenTemplate={() => setTemplateDialogOpen(true)}
            isEditQuoteRequest={isEditQuoteRequest}
            showMobileMenu={showMobileMenu}
            isEditShipment={isEditShipment}
            isEditQuickRequest={isEditQuickRequest}
            headingMessageId={headerHeadingMessageId}
            showProviderSummary={!isQuickRequest}
          />
        )}

        {isReviewMode && (
          <div className="rfv2-review-page-title">
            <FormattedMessage id="request-form.confirmation" defaultMessage="Confirmation" />
          </div>
        )}

        {isReviewMode && isEditQuoteRequest && quotesAvailable && (
          <div className="rfv2-edit-quote-warning">
            <Alert
              variant="warning"
              label={intl.formatMessage({
                id: "new-request.form.editBookingError",
                defaultMessage: "After submitting these changes, you won't be able to book the existing quotes",
              })}
            >
              <FormattedMessage
                id="new-request.form.editBookingSubtext"
                defaultMessage="Providers will be asked to review the updated request to confirm or revise their quotations"
              />
            </Alert>
          </div>
        )}

        <div
          className={classNames("rfv2-page", {
            "rfv2-page--review": isReviewMode,
            "rfv2-page--mobile-menu-visible": showMobileMenu,
          })}
          data-testid={testIds.REQUEST_FORM_CONTAINER}
          style={pageStyle}
        >
          <div className={classNames("rfv2-edit-view", { "rfv2-edit-view--hidden": isReviewMode })}>
            <Accordion
              key={accordionKey}
              preExpanded={expandedUuids}
              allowZeroExpanded
              allowMultipleExpanded
              onChange={(uuids) => setExpandedUuids(uuids.map(String))}
              className="rfv2-accordions"
            >
              {requestFormSections.map(({ key, defaultMessage, component: SectionComponent }) => {
                const isOrganisation = key === "request-form.organisation";
                const badge = renderAccordionBadge(
                  resolveAccordionBadge({
                    sectionKey: key,
                    hasValidationError: erroredSections.includes(key),
                    hasAttemptedReview,
                    hasPaymentLocation,
                    hasScheduleAddresses,
                    hasPackingList: hasPackingListEntries,
                    hasFreightMode,
                    hasDeadlinesAndProviders,
                  }),
                );

                return (
                  <RequestFormAccordion
                    key={key}
                    uuid={key}
                    title={intl.formatMessage({ id: key, defaultMessage })}
                    badge={badge}
                    subHeaderBadge={
                      key === "request-form.packingList" && hasMissingStopsWarning ? (
                        <MissingStopsWarningBadge message={missingStopsWarningMessage} />
                      ) : undefined
                    }
                    disabled={!isOrganisation && !hasPaymentLocation}
                  >
                    {SectionComponent && (isOrganisation || hasPaymentLocation) && <SectionComponent />}
                  </RequestFormAccordion>
                );
              })}
            </Accordion>
          </div>
          {isReviewMode && <ReviewPage showProviderSummary={!isQuickRequest} />}
        </div>
        <RequestFormFooter
          isReviewMode={isReviewMode}
          hasPaymentLocation={hasPaymentLocation}
          isSubmitting={isSubmitting}
          isReviewValidating={isReviewValidating}
          {...footerProps}
        />
      </RequestFormCalculationsProvider>
    </RequestFormReviewValidationContext.Provider>
  );
}

function RequestFormV2Content({
  methods,
  isDraftLoading,
  isEditQuoteRequest,
  isEditShipment,
  isEditQuickRequest,
}: RequestFormV2ContentProps) {
  return (
    <RequestFormSaveProvider>
      <RequestFormV2SaveBody
        methods={methods}
        isDraftLoading={isDraftLoading}
        isEditQuoteRequest={isEditQuoteRequest}
        isEditShipment={isEditShipment}
        isEditQuickRequest={isEditQuickRequest}
      />
    </RequestFormSaveProvider>
  );
}

type RequestFormV2Props = {
  isEditQuoteRequest?: boolean;
  isEditShipment?: boolean;
  isQuickRequest?: boolean;
  isEditQuickRequest?: boolean;
};

function RequestFormV2Form({
  isEditQuoteRequest = false,
  isEditShipment = false,
  isEditQuickRequest = false,
}: RequestFormV2Props) {
  const mode = useRequestFormMode();
  const isQuickRequest = mode === "quick";
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const parsedPermissions = JSON.parse(permissions || "{}");
  const canCreateQuoteRequests = parsedPermissions?.permissions?.quoting?.create_quote_requests;
  const canCreateQuickRequests = parsedPermissions?.permissions?.quoting?.create_quick_requests;
  const { setNavigationConfig } = useContext(NavContext);
  const methods = useForm({
    defaultValues: getRequestFormV2DefaultValues(),
  });
  const { isLoading: isDraftLoading, loadFailed } = useRequestFormDraftLoad({
    methods,
    isEditQuickRequest,
  });
  const { isBootstrapping, bootstrapFailed } = useQuickRequestEditBootstrap({
    isEditQuickRequest,
    enabled: Boolean(canCreateQuickRequests),
  });
  const isLoading = isDraftLoading || isBootstrapping;

  useEffect(() => {
    if (isEditShipment) return;

    if (isQuickRequest && !canCreateQuickRequests) {
      navigate("/access-denied");
      return;
    }

    if (!isQuickRequest && !canCreateQuoteRequests) {
      navigate("/access-denied");
    }
  }, [canCreateQuickRequests, canCreateQuoteRequests, isEditShipment, isQuickRequest, navigate]);

  useEffect(() => {
    setRequestFormAnalyticsContext({
      formFlow: getFormFlow({ isEditQuoteRequest, isEditShipment, isQuickRequest, isEditQuickRequest }),
    });
  }, [isEditQuoteRequest, isEditShipment, isQuickRequest, isEditQuickRequest]);

  useEffect(() => {
    document.body.classList.add("rfv2-page-active");
    setNavigationConfig((prev: Record<string, unknown>) => ({ ...prev, isRequestFormV2Page: true }));
    return () => {
      document.body.classList.remove("rfv2-page-active");
      setNavigationConfig((prev: Record<string, unknown>) => ({
        ...prev,
        isRequestFormV2Page: false,
        isRequestFormV2ReviewMode: false,
      }));
    };
  }, [setNavigationConfig]);

  if (isEditQuickRequest && (bootstrapFailed || loadFailed)) {
    return null;
  }

  return (
    <EditShipmentLockProvider value={isEditShipment}>
      <FormProvider {...methods}>
        <RequestFormV2Content
          methods={methods}
          isDraftLoading={isLoading}
          isEditQuoteRequest={isEditQuoteRequest}
          isEditShipment={isEditShipment}
          isEditQuickRequest={isEditQuickRequest}
        />
      </FormProvider>
    </EditShipmentLockProvider>
  );
}

export default function RequestFormV2({
  isEditQuoteRequest = false,
  isEditShipment = false,
  isQuickRequest = false,
  isEditQuickRequest = false,
}: RequestFormV2Props) {
  return (
    <RequestFormModeProvider value={isQuickRequest ? "quick" : "quote"}>
      <RequestFormV2Form
        isEditQuoteRequest={isEditQuoteRequest}
        isEditShipment={isEditShipment}
        isEditQuickRequest={isEditQuickRequest}
      />
    </RequestFormModeProvider>
  );
}
